import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Detects a new deployment for a long-open SPA tab and auto-refreshes safely.
 *
 * The custom service worker (`public/sw.js` v4) keeps a *navigated* page fresh
 * (network-first index.html → new hashed chunks), but a tab left open never
 * fires a navigation request, so it keeps running the stale in-memory JS bundle
 * until a manual reload. This hook closes that gap at the app level — no new
 * service worker — by polling a build id and reloading at safe moments.
 *
 * Behaviour:
 * - Captures the *loaded* build id once (injected via `import.meta.env.VITE_BUILD_ID`).
 * - Polls a cache-busted `/version.json` on an interval, and on tab refocus /
 *   visibility-visible; polling is paused while the tab is hidden.
 * - When the deployed build id differs from the loaded one → `updateAvailable`.
 * - Failure-safe: any network/parse error or missing/blank id is a no-op — it
 *   never throws, never white-screens, and never false-positives a reload.
 * - Reloads at most once per detected build id (sessionStorage guard) so a
 *   mis-served version.json can never cause a reload loop.
 */

const VERSION_URL = "/version.json";
const DEFAULT_POLL_INTERVAL_MS = 90_000; // 90s
const RELOAD_GUARD_KEY = "version-reload-attempt";

interface VersionResponse {
  buildId: string;
}

/** Narrows an unknown JSON payload to a well-formed `{ buildId: string }`. */
function isVersionResponse(data: unknown): data is VersionResponse {
  return (
    typeof data === "object" &&
    data !== null &&
    "buildId" in data &&
    typeof (data as { buildId: unknown }).buildId === "string"
  );
}

/**
 * Reload onto the newly-deployed build — reliably. A bare `location.reload()`
 * stays under the *current* (now-stale) service worker, which can keep serving
 * the old app from its caches; that is exactly why users had to hard-refresh
 * manually to see freshly-saved data. So before reloading we:
 *
 *   1. Update the registration and tell a waiting worker to `skipWaiting()`,
 *      so the new build controls the next navigation.
 *   2. Delete the runtime caches, so cached HTML/chunks can't win over network.
 *   3. Reload.
 *
 * Every step is failure-safe and guarded: if the SW / Cache Storage APIs are
 * absent or throw, we still fall through to `reload()`, so the user is never
 * left stuck. When neither API is present (e.g. unit tests) no `await` runs and
 * the reload is synchronous.
 */
async function reloadOntoFreshBuild(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        await reg.update().catch(() => {});
        reg.waiting?.postMessage({ type: "SKIP_WAITING" });
      }
    }
  } catch {
    // ignore — the reload below still recovers the user.
  }
  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // ignore — busting caches is best-effort, not required for correctness.
  }
  window.location.reload();
}

export interface UseVersionCheckOptions {
  /**
   * Returns true when reloading would lose user work (dirty form, upload,
   * editing modal). The safe-reload triggers defer while this is true.
   * Defaults to "never unsafe".
   */
  isUnsafe?: () => boolean;
  /** Poll interval in ms. Defaults to 90s. */
  pollIntervalMs?: number;
}

export interface UseVersionCheckResult {
  /** A different build has been deployed since this tab loaded. */
  updateAvailable: boolean;
  /** The build id currently loaded in this tab (null in environments without the inject). */
  loadedBuildId: string | null;
  /** The newly-deployed build id once detected, else null. */
  deployedBuildId: string | null;
  /** Reload now regardless of unsaved work (for an explicit "Refresh now" action). */
  reloadNow: () => void;
  /** Reload only if there is no unsaved work and we have not already reloaded for this build. */
  reloadIfSafe: () => void;
}

export function useVersionCheck(
  options: UseVersionCheckOptions = {},
): UseVersionCheckResult {
  const { isUnsafe, pollIntervalMs = DEFAULT_POLL_INTERVAL_MS } = options;

  const loadedBuildId =
    (import.meta.env.VITE_BUILD_ID as string | undefined) ?? null;

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [deployedBuildId, setDeployedBuildId] = useState<string | null>(null);
  const deployedIdRef = useRef<string | null>(null);

  // Reload at most once per build id. The guard is keyed by the *target* build
  // id, so if a reload doesn't actually change the loaded id (mis-served
  // version.json) we detect the same id again and skip — no loop.
  const doReload = useCallback((targetId: string) => {
    try {
      if (sessionStorage.getItem(RELOAD_GUARD_KEY) === targetId) return;
      sessionStorage.setItem(RELOAD_GUARD_KEY, targetId);
    } catch {
      // sessionStorage unavailable (private mode / disabled) — reload once.
    }
    // The guard above is set synchronously (so the once-per-build loop guard
    // holds), then the SW-aware reload runs fire-and-forget.
    void reloadOntoFreshBuild();
  }, []);

  const reloadNow = useCallback(() => {
    const target = deployedIdRef.current;
    if (target) doReload(target);
  }, [doReload]);

  const reloadIfSafe = useCallback(() => {
    const target = deployedIdRef.current;
    if (!target) return;
    if (isUnsafe?.()) return;
    doReload(target);
  }, [doReload, isUnsafe]);

  // Fetch /version.json and compare. Returns true if an update is available.
  // Always resolves — never rejects — so callers can chain safely.
  const check = useCallback(async (): Promise<boolean> => {
    if (!loadedBuildId) return false;
    try {
      const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        cache: "no-store",
      });
      if (!res.ok) return false;
      const data: unknown = await res.json();
      const id = isVersionResponse(data) ? data.buildId : null;
      if (!id || id === loadedBuildId) return false;
      deployedIdRef.current = id;
      setDeployedBuildId(id);
      setUpdateAvailable(true);
      return true;
    } catch {
      // Network error / unparseable / offline — stay silent.
      return false;
    }
  }, [loadedBuildId]);

  useEffect(() => {
    // No loaded build id (e.g. inject missing) → feature is inert.
    if (!loadedBuildId) return;

    let intervalId: ReturnType<typeof setInterval> | undefined;

    const startPolling = () => {
      if (intervalId !== undefined) return;
      intervalId = setInterval(() => {
        void check();
      }, pollIntervalMs);
    };
    const stopPolling = () => {
      if (intervalId !== undefined) {
        clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    // Returning to a stale tab is exactly the case this feature targets: check,
    // and if a new build is live, refresh at this safe moment (unless unsafe).
    const checkAndMaybeReload = () => {
      void check().then((available) => {
        if (available) reloadIfSafe();
      });
    };

    const onVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
        checkAndMaybeReload();
      }
    };
    const onFocus = () => checkAndMaybeReload();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);

    // Kick off immediately if the tab is currently visible.
    if (!document.hidden) {
      startPolling();
      void check();
    }

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [loadedBuildId, pollIntervalMs, check, reloadIfSafe]);

  return {
    updateAvailable,
    loadedBuildId,
    deployedBuildId,
    reloadNow,
    reloadIfSafe,
  };
}
